import React from 'react';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import { Dialog } from '../Dialog/Dialog.js';
import { IDialogProps } from '../Dialog/models/index.js';

import { IConfirmDialogConfiguration, IConfirmDialogProps, IConfirmDialogState } from './models/index.js';
import ButtonContainer from './styledComponents/ButtonContainer.js';
import CancelButton from './styledComponents/CancelButton.js';
import ConfirmButton from './styledComponents/ConfirmButton.js';
import Header from './styledComponents/Header.js';
import Message from './styledComponents/Message.js';
import Wrapper from './styledComponents/Wrapper.js';
import { CONFIRM_DIALOG_CATEGORY_KEY, CONFIRM_DIALOG_COMPONENT_KEY } from './ConfirmDialog.meta.js';

export class ConfirmDialog extends ComponentBase<
	IConfirmDialogProps,
	IConfirmDialogState,
	IConfirmDialogConfiguration
> {
	categoryKey = CONFIRM_DIALOG_CATEGORY_KEY;
	componentKey = CONFIRM_DIALOG_COMPONENT_KEY;

	isOpen = true;

	constructor(props: IConfirmDialogProps) {
		super(props);

		makeObservable(this, {
			isOpen: observable,
			confirm: action,
			cancel: action,
			confirmButtonText: computed,
			cancelButtonText: computed,
			dialogProps: computed,
			setIsOpen: action,
		});

		this.isOpen = props.isOpen || true;
	}

	confirm = () => {
		const { onConfirm } = this.props;
		if (onConfirm) {
			onConfirm();
		}
		this.isOpen = false;
	};

	cancel = () => {
		const { onCancel } = this.props;
		if (onCancel) {
			onCancel();
		}
		this.isOpen = false;
	};

	get confirmButtonText(): string {
		const { confirmButtonText } = this.props;
		return confirmButtonText || 'Confirm';
	}

	get cancelButtonText(): string {
		const { cancelButtonText } = this.props;
		return cancelButtonText || 'Cancel';
	}

	get dialogProps(): IDialogProps {
		return {
			isOpen: this.isOpen,
		};
	}

	componentDidUpdate() {
		const { isOpen } = this.props;
		if (isOpen !== undefined && this.isOpen !== isOpen) {
			this.setIsOpen(isOpen);
		}
	}

	setIsOpen = (isOpen: boolean) => {
		this.isOpen = isOpen;
	};

	render() {
		const { className, heading, message } = this.props;

		// eslint-disable-next-line @typescript-eslint/naming-convention
		const dangerouslySetInnerHTML = { __html: message };
		return (
			<Dialog {...this.dialogProps}>
				<Wrapper className={className}>
					<Header>{heading}</Header>
					<Message dangerouslySetInnerHTML={dangerouslySetInnerHTML} />
					<ButtonContainer>
						<ConfirmButton
							text={this.confirmButtonText}
							onClick={this.confirm}
							data-qa-ref="confirm-button"
						/>
						<CancelButton text={this.cancelButtonText} onClick={this.cancel} data-qa-ref="cancel-button" />
					</ButtonContainer>
				</Wrapper>
			</Dialog>
		);
	}
}

export default InjectedStyledComponent(
	observer(ConfirmDialog),
	CONFIRM_DIALOG_CATEGORY_KEY,
	CONFIRM_DIALOG_COMPONENT_KEY,
);
